import { defineStore } from "pinia"
import { computed, ref } from "vue"
import { useBrainStore } from "@/features/brain/stores/brain.store"

export const useTestTimerStore = defineStore("testTimer", () => {
  const time = ref(0)
  const totalDuration = ref(0)
  const isActive = ref(false)
  const intervalRef = ref()
  const timerCallback = ref(() => {})
  const startTime = ref<number | null>(null) // Track when timer started
  const pausedTime = ref(0) // Track accumulated time when paused

  function startTimer(duration: number, callback?: () => void) {
    time.value = duration
    totalDuration.value = duration
    pausedTime.value = duration

    if (!isActive.value) {
      if (callback) timerCallback.value = callback
      isActive.value = true
      startTime.value = Date.now()

      // Use setInterval with timestamp to avoid navigator throttle on inactive tab
      intervalRef.value = setInterval(() => {
        if (!startTime.value) return

        const elapsed = Math.floor((Date.now() - startTime.value) / 1000)
        const remaining = pausedTime.value - elapsed

        if (remaining > 0) {
          time.value = remaining

          if (time.value % 20 === 0 && time.value !== pausedTime.value) {
            useBrainStore().setDropzonesBlink()
          }
        } else {
          time.value = 0
          clearInterval(intervalRef.value)
          intervalRef.value = null
          isActive.value = false
          startTime.value = null

          if (callback) {
            timerCallback.value() // Call callback when timer is done
          } else {
            useBrainStore().userIsIdle()
          }
        }
      }, 250)
    }
  }

  function stopTimer() {
    if (intervalRef.value) {
      if (startTime.value) {
        const elapsed = Math.floor((Date.now() - startTime.value) / 1000)
        pausedTime.value = Math.max(0, pausedTime.value - elapsed)
      }

      clearInterval(intervalRef.value)
      intervalRef.value = null
      isActive.value = false
      startTime.value = null
    }
  }

  function resetTimer() {
    time.value = 0
    pausedTime.value = 0
    stopTimer()
  }

  function resumeTimer() {
    if (!isActive.value && time.value > 0) {
      pausedTime.value = time.value
      startTimer(time.value, timerCallback.value)
    }
  }

  const getTimerBehaviour = computed(() => {
    const percentage = (time.value / totalDuration.value) * 100

    const behaviour =
      percentage > 25
        ? "normal"
        : percentage > 10
          ? "warning"
          : percentage > 0
            ? "danger"
            : "normal"

    return behaviour
  })

  const getFormattedTime = computed(() => {
    if (time.value <= 0) return "--:--"

    const minutes = Math.floor(time.value / 60)
      .toString()
      .padStart(2, "0")
    const seconds = (time.value % 60).toString().padStart(2, "0")
    return `${minutes}:${seconds}`
  })

  const getElapsedTime = computed(() => {
    return totalDuration.value - time.value
  })

  return {
    time,
    isActive,
    startTimer,
    stopTimer,
    resetTimer,
    resumeTimer,
    getFormattedTime,
    getTimerBehaviour,
    getElapsedTime,
  }
})
